import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const PerformanceMonitoringPanel = ({ onExportData }) => {
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedMetric, setSelectedMetric] = useState('win_rate');
  const [realTimeData, setRealTimeData] = useState({
    totalBids: 1247,
    wonBids: 423,
    winRate: 33.9,
    avgCPA: 42.75,
    totalSpent: 18084.25,
    leadsAcquired: 423,
    qualityScore: 8.7,
    avgCallLength: 4.2
  });

  const bidActivityData = [
    { time: '00:00', bids: 45, wins: 12, losses: 33 },
    { time: '04:00', bids: 32, wins: 8, losses: 24 },
    { time: '08:00', bids: 78, wins: 28, losses: 50 },
    { time: '12:00', bids: 95, wins: 35, losses: 60 },
    { time: '16:00', bids: 112, wins: 42, losses: 70 },
    { time: '20:00', bids: 89, wins: 31, losses: 58 }
  ];

  const performanceTrendData = [
    { date: '08/12', winRate: 31.2, cpa: 45.30, quality: 8.2, callLength: 3.8 },
    { date: '08/13', winRate: 29.8, cpa: 47.15, quality: 8.0, callLength: 4.1 },
    { date: '08/14', winRate: 35.1, cpa: 41.20, quality: 8.5, callLength: 4.5 },
    { date: '08/15', winRate: 32.7, cpa: 43.85, quality: 8.3, callLength: 4.2 },
    { date: '08/16', winRate: 36.4, cpa: 39.90, quality: 8.8, callLength: 4.7 },
    { date: '08/17', winRate: 34.2, cpa: 42.10, quality: 8.6, callLength: 4.3 },
    { date: '08/18', winRate: 33.9, cpa: 42.75, quality: 8.7, callLength: 4.2 }
  ];

  const leadQualityDistribution = [
    { name: 'Excellent', value: 35, color: '#059669' },
    { name: 'Good', value: 42, color: '#0EA5E9' },
    { name: 'Fair', value: 18, color: '#D97706' },
    { name: 'Poor', value: 5, color: '#DC2626' }
  ];

  const recentBids = [
    {
      id: 'AI-2024-0818-001',
      timestamp: new Date(Date.now() - 2 * 60 * 1000),
      amount: 45.50,
      status: 'won',
      leadType: 'Auto Insurance',
      state: 'CA',
      age: 32,
      quality: 9.2,
      callLength: 5.2
    },
    {
      id: 'HO-2024-0818-002',
      timestamp: new Date(Date.now() - 8 * 60 * 1000),
      amount: 38.25,
      status: 'lost',
      leadType: 'Home Insurance',
      state: 'TX',
      age: 45,
      quality: 8.5,
      callLength: 3.1
    },
    {
      id: 'AI-2024-0818-003',
      timestamp: new Date(Date.now() - 15 * 60 * 1000),
      amount: 42.00,
      status: 'won',
      leadType: 'Auto Insurance',
      state: 'FL',
      age: 28,
      quality: 8.8,
      callLength: 4.8
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setRealTimeData(prev => ({
        ...prev,
        totalBids: prev?.totalBids + Math.floor(Math.random() * 3),
        wonBids: prev?.wonBids + Math.floor(Math.random() * 2),
        winRate: ((prev?.wonBids + Math.floor(Math.random() * 2)) / (prev?.totalBids + Math.floor(Math.random() * 3)) * 100),
        totalSpent: prev?.totalSpent + (Math.random() * 50),
        avgCallLength: prev?.avgCallLength + (Math.random() * 0.2 - 0.1)
      }));
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    return timestamp?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'won':
        return 'text-success bg-success/10';
      case 'lost':
        return 'text-error bg-error/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className="h-full flex flex-col bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="BarChart3" size={20} className="text-accent" />
          <h2 className="text-lg font-semibold text-foreground">Performance Monitoring</h2>
        </div>
        <div className="flex items-center space-x-2">
          <Select
            options={[
              { value: '1h', label: 'Last Hour' },
              { value: '24h', label: 'Last 24 Hours' },
              { value: '7d', label: 'Last 7 Days' },
              { value: '30d', label: 'Last 30 Days' }
            ]}
            value={timeRange}
            onChange={setTimeRange}
            className="w-32"
          />
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={onExportData}
          >
            Export
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="RefreshCw"
            title="Refresh data"
          />
        </div>
      </div>
      {/* Key Metrics - Enhanced with Call Length */}
      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">{realTimeData?.totalBids?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">Total Bids</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">{realTimeData?.wonBids?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">Won Bids</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent">{realTimeData?.winRate?.toFixed(1)}%</div>
            <div className="text-xs text-muted-foreground">Win Rate</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">${realTimeData?.avgCPA?.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">Avg CPA</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">{realTimeData?.avgCallLength?.toFixed(1)}m</div>
            <div className="text-xs text-muted-foreground">Avg Call</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">${realTimeData?.totalSpent?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">Total Spent</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">{realTimeData?.leadsAcquired?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">Leads</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">{realTimeData?.qualityScore?.toFixed(1)}</div>
            <div className="text-xs text-muted-foreground">Quality</div>
          </div>
        </div>
      </div>
      {/* Charts Section */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Bid Activity Chart */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-sm font-medium text-foreground mb-4">Bid Activity (24h)</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bidActivityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="time" stroke="var(--color-muted-foreground)" fontSize={12} />
                  <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-card)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="wins" fill="var(--color-success)" name="Wins" />
                  <Bar dataKey="losses" fill="var(--color-error)" name="Losses" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Performance Trend */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-sm font-medium text-foreground mb-4">Win Rate Trend</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="date" stroke="var(--color-muted-foreground)" fontSize={12} />
                  <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-card)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="winRate" 
                    stroke="var(--color-accent)" 
                    strokeWidth={2}
                    dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 4 }}
                    name="Win Rate (%)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lead Quality Distribution */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-sm font-medium text-foreground mb-4">Lead Quality Distribution</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={leadQualityDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {leadQualityDistribution?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {leadQualityDistribution?.map((item) => (
                <div key={item?.name} className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: item?.color }}
                  />
                  <span className="text-xs text-muted-foreground">{item?.name}: {item?.value}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Bid Activity - Enhanced with Call Length */}
          <div className="lg:col-span-2 bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-foreground">Recent Bid Activity</h3>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full pulse-live" />
                <span className="text-xs text-muted-foreground">Live</span>
              </div>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {recentBids?.map((bid) => (
                <div key={bid?.id} className="flex items-center justify-between p-2 bg-card rounded border border-border">
                  <div className="flex items-center space-x-3">
                    <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(bid?.status)}`}>
                      {bid?.status?.toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{bid?.id}</div>
                      <div className="text-xs text-muted-foreground">
                        {bid?.leadType} • {bid?.state} • Age {bid?.age} • {bid?.callLength?.toFixed(1)}m call
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-foreground">${bid?.amount}</div>
                    <div className="text-xs text-muted-foreground">{formatTimestamp(bid?.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMonitoringPanel;